import type { ColumnDef } from '@tanstack/react-table';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import {
  createActionsColumn,
  createSerialColumn,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowEditDeleteButtons } from '@/components/common/RowActions';
import { StatusBadge } from '@/components/common/StatusBadge';
import { CODE_BADGE_CLASS } from '@/styles/codeBadge';
import { formatDisplayDate } from '@/utils/date';
import { EIIN_APPLICABLE_LABELS } from '../constants/instituteType.constants';
import type { InstituteType } from '../types/instituteType.types';

interface InstituteTypeColumnActions {
  onEdit?: (instituteType: InstituteType) => void;
  onDelete?: (instituteType: InstituteType) => void;
}

export function getInstituteTypeColumns(
  searchText?: string,
  actions?: InstituteTypeColumnActions,
): ColumnDef<InstituteType, unknown>[] {
  const hasActions = actions?.onEdit !== undefined || actions?.onDelete !== undefined;

  return [
    createSerialColumn<InstituteType>(),
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => {
        const code = row.original.code;
        if (!code) return <EmptyValue />;
        return (
          <WrappingCellText className={CODE_BADGE_CLASS}>
            <HighlightText text={code} highlightedText={searchText} />
          </WrappingCellText>
        );
      },
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      accessorKey: 'requiresEiin',
      header: 'EIIN Applicable',
      cell: ({ row }) => {
        if (row.original.requiresEiin) {
          return <StatusBadge tone="info">{EIIN_APPLICABLE_LABELS.applicable}</StatusBadge>;
        }
        return <StatusBadge tone="neutral">{EIIN_APPLICABLE_LABELS.notApplicable}</StatusBadge>;
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => (
        <span className="text-sm whitespace-nowrap">
          {formatDisplayDate(row.original.createdAt)}
        </span>
      ),
    },
    ...(hasActions
      ? [
          createActionsColumn<InstituteType>((instituteType) => (
            <RowEditDeleteButtons
              item={instituteType}
              itemName={instituteType.name}
              onEdit={actions?.onEdit}
              onDelete={actions?.onDelete}
            />
          )),
        ]
      : []),
  ];
}
